import React from 'react';
import { GovIcon } from '@gov-design-system-ce/react';
import type { DokladyDokladDto } from '@gov-nx/api';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useDokladyContext } from '@gov-nx/module/page';
import {
	CopyButton,
	ErrorStatusBanner,
	InfoBanner,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { isInPastOrNearFuture, toDateReadable } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';
import { DocumentSkeleton } from './IdentityDocumentListSkeleton';

interface DocumentTileProps {
	code: PageCode;
	document: DokladyDokladDto;
}

const DocumentTile = ({ document, code }: DocumentTileProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const links: Record<string, string> = {
		ID: pageRouteResolver('profil.doklady.obcansky-prukaz'),
		P: pageRouteResolver('profil.doklady.cestovni-pas'),
		RP: pageRouteResolver('profil.doklady.ridicsky-prukaz'),
	};
	const showWarning = isInPastOrNearFuture(document.platnostDo);

	return (
		<Tile
			icon={{ name: 'chevron-right', type: 'components' }}
			to={document.druhDokladu ? links[document.druhDokladu] : '#'}
			name={tsn('typ.' + document.druhDokladu)}
			headlineLink={true}>
			<div className="flex flex-col justify-start gap-2 [ sm:flex-row sm:items-center ]">
				<div className="flex items-center gap-1">
					<span className="text-(--text-secondary) inline-block mr-1!">
						{tsn('cislo')}:
					</span>
					<strong className="text-(--text-primary)">
						{document.cisloDokladu}
					</strong>
					<CopyButton
						label={
							tsn('akce.kopirovat-cislo-dokladu') + ' ' + document.cisloDokladu
						}
						value={document.cisloDokladu as string}
					/>
				</div>
				{document.platnostOd ? (
					<div className="flex items-center gap-1">
						<span className="text-(--text-secondary) inline-block mr-1!">
							{tsn('platnost-od')}:
						</span>
						<strong className="text-(--text-primary)">
							{toDateReadable(document.platnostOd)}
						</strong>
					</div>
				) : null}
				{document.platnostDo ? (
					<div className="flex items-center gap-1">
						<span className="text-(--text-secondary) inline-block mr-1!">
							{tsn('platnost-do')}:
						</span>
						<strong className="text-(--text-primary)">
							{toDateReadable(document.platnostDo)}
						</strong>
						{showWarning && (
							<GovIcon
								type="components"
								name="exclamation-triangle-fill"
								slot="icon"
								color="warning"
							/>
						)}
					</div>
				) : null}
			</div>
		</Tile>
	);
};

export interface IdentityDocumentListProps {
	code: PageCode;
}

export const IdentityDocumentList = ({ code }: IdentityDocumentListProps) => {
	const {
		documents,
		driversLicense,
		hasLicense,
		documentsError,
		isDocumentsLoading,
		driversLicenseError,
		isDriversLicenseLoading,
	} = useDokladyContext();

	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);

	const allDocuments = [...documents, driversLicense];
	if (allDocuments.length === 0) {
		return (
			<InfoBanner
				headline={tsn('data.error.zadny-zaznam')}
				text={tsn('data.error.popis')}
			/>
		);
	}

	return (
		<Tiles>
			{documentsError ? (
				<ErrorStatusBanner code={code} />
			) : isDocumentsLoading ? (
				<DocumentSkeleton />
			) : (
				documents.map((document) => (
					<DocumentTile
						key={document.cisloDokladu}
						document={document}
						code={code}
					/>
				))
			)}
			{driversLicenseError ? (
				<ErrorStatusBanner code={code} />
			) : isDriversLicenseLoading ? (
				<DocumentSkeleton />
			) : hasLicense ? (
				driversLicense && (
					<DocumentTile
						document={driversLicense}
						code={code}
					/>
				)
			) : null}
		</Tiles>
	);
};
